import { type FC, useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Swords, Users, DollarSign, ListOrdered, Loader2, X } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, Badge, Button } from '@/components/ui';
import { formatPrice } from '@/lib/utils/formatting';
import { getActivePrice } from '@/lib/utils/stripe';
import { useWaitlist } from '@/hooks/data/useWaitlist';
import { useAuth } from '@/hooks';
import { REGISTRATIONS_CLOSED } from '@/lib/registrationsClosed';
import { ClosedNotice } from '@/components/shared';
import type { Tournament } from '@/types';

export interface TournamentCardProps {
  tournament: Tournament;
  onRegister?: (tournamentId: string) => void;
}

/**
 * Tournament card component displaying tournament details
 * Shows weapon type, division, registration info, and status
 */
export const TournamentCard: FC<TournamentCardProps> = ({ tournament, onRegister }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { joinWaitlist, leaveWaitlist, getUserWaitlistEntry, getWaitlistCount } = useWaitlist();

  const [isOnWaitlist, setIsOnWaitlist] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const [waitingCount, setWaitingCount] = useState(0);

  useEffect(() => {
    let cancelled = false;
    void getWaitlistCount(tournament.id).then((count) => {
      if (!cancelled) setWaitingCount(count);
    });
    return () => { cancelled = true; };
  }, [tournament.id, getWaitlistCount]);

  const spotsRemaining = tournament.maxParticipants - tournament.currentParticipants;
  const effectiveAvailable = spotsRemaining - tournament.waitlistHeldSpots - waitingCount;
  const isAtCapacity = effectiveAvailable <= 0;
  const isFullyBooked = tournament.status === 'full' || isAtCapacity;
  const isRegistrationOpen = tournament.status === 'open';
  const canShowRegisterButton = isRegistrationOpen && !isFullyBooked && !REGISTRATIONS_CLOSED;
  const { price, isEarlyBird } = getActivePrice(tournament);
  const percentFull = (tournament.currentParticipants / tournament.maxParticipants) * 100;

  // Check if user is on waitlist for this tournament
  const checkWaitlistStatus = useCallback(async () => {
    if (!user || !isFullyBooked) {
      setIsOnWaitlist(false);
      return;
    }

    const entry = await getUserWaitlistEntry(tournament.id);
    setIsOnWaitlist(!!entry);
  }, [user, isFullyBooked, tournament.id, getUserWaitlistEntry]);

  useEffect(() => {
    void checkWaitlistStatus();
  }, [checkWaitlistStatus]);

  const handleJoinWaitlist = async (): Promise<void> => {
    if (!user) {
      navigate('/login', { state: { returnTo: `/events` } });
      return;
    }

    setActionLoading(true);
    setWaitlistError(null);

    const result = await joinWaitlist(tournament.id);

    if (result.success) {
      setIsOnWaitlist(true);
    } else {
      setWaitlistError(result.error || 'Failed to join waitlist');
    }

    setActionLoading(false);
  };

  const handleLeaveWaitlist = async (): Promise<void> => {
    setActionLoading(true);
    setWaitlistError(null);

    const result = await leaveWaitlist(tournament.id);

    if (result.success) {
      setIsOnWaitlist(false);
    } else {
      setWaitlistError(result.error || 'Failed to leave waitlist');
    }

    setActionLoading(false);
  };

  const getStatusBadge = (): JSX.Element => {
    switch (tournament.status) {
      case 'open':
        return <Badge variant="success">Registration Open</Badge>;
      case 'closed':
        return <Badge variant="warning">Registration Closed</Badge>;
      case 'full':
        return <Badge variant="warning">Full</Badge>;
      case 'completed':
        return <Badge variant="secondary">Completed</Badge>;
      case 'draft':
        return <Badge variant="outline">Draft</Badge>;
      default:
        return <Badge variant="outline">{tournament.status}</Badge>;
    }
  };

  const getCapacityBadge = (): JSX.Element | null => {
    if (!isRegistrationOpen) return null;

    if (isFullyBooked) {
      return <Badge variant="destructive">SOLD OUT</Badge>;
    }

    if (effectiveAvailable <= 5) {
      return <Badge variant="warning">Only {effectiveAvailable} spots left!</Badge>;
    }

    if (percentFull >= 75) {
      return <Badge variant="secondary">Filling fast</Badge>;
    }

    return null;
  };

  const handleRegisterClick = (): void => {
    if (onRegister && canShowRegisterButton) {
      onRegister(tournament.id);
    }
  };

  return (
    <Card className="bg-white border-turquoise-200 shadow-md hover:shadow-lg hover:shadow-orange-500/20 transition-all duration-200">
      <CardHeader>
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-2">
            <Swords className="h-5 w-5 text-orange-500" />
            <Link to={`/tournaments/${tournament.id}`} className="hover:opacity-80 transition-opacity">
              <CardTitle className="text-xl font-viking text-turquoise-900 hover:text-orange-600 transition-colors">
                {tournament.name}
              </CardTitle>
            </Link>
          </div>
          {getStatusBadge()}
        </div>
        <div className="flex gap-2 mt-2">
          <Badge variant="outline">{tournament.weapon}</Badge>
          <Badge variant="outline">{tournament.division}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-3">
          {/* Capacity badge */}
          {getCapacityBadge() && (
            <div className="flex justify-center">
              {getCapacityBadge()}
            </div>
          )}

          {/* Participant count */}
          <div className="flex items-center gap-2 text-sm">
            <Users className="h-4 w-4 text-orange-500" />
            <span className="text-turquoise-700">
              {tournament.currentParticipants} / {tournament.maxParticipants} registered
            </span>
          </div>

          {/* Held spots indicator */}
          {tournament.waitlistHeldSpots > 0 && (
            <div className="flex items-center gap-2 text-sm">
              <ListOrdered className="h-4 w-4 text-orange-500" />
              <span className="text-orange-600">
                {tournament.waitlistHeldSpots} {tournament.waitlistHeldSpots === 1 ? 'spot' : 'spots'} held for waitlist
              </span>
            </div>
          )}

          {/* Progress bar */}
          {isRegistrationOpen && (
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div
                className={`h-2 rounded-full transition-all ${
                  percentFull >= 90
                    ? 'bg-red-500'
                    : percentFull >= 75
                    ? 'bg-orange-500'
                    : 'bg-turquoise-500'
                }`}
                style={{ width: `${Math.min(percentFull, 100)}%` }}
              />
            </div>
          )}

          {/* Registration fee */}
          <div className="flex items-center gap-2 text-sm">
            <DollarSign className="h-4 w-4 text-orange-500" />
            <div className="flex items-center gap-2 flex-wrap">
              {isEarlyBird && (
                <Badge variant="default" className="bg-orange-500 text-white">
                  Early Bird!
                </Badge>
              )}
              <span className="text-turquoise-700">
                Registration Fee: {formatPrice(price)}
              </span>
              {isEarlyBird && tournament.registrationFee > price && (
                <span className="text-sm text-gray-400 line-through">
                  {formatPrice(tournament.registrationFee)}
                </span>
              )}
            </div>
          </div>
          {isEarlyBird && tournament.earlyBirdEndDate && (
            <div className="text-xs text-orange-600 ml-6">
              Early bird pricing ends {new Date(tournament.earlyBirdEndDate).toLocaleDateString()}
            </div>
          )}

          {/* Waitlist error */}
          {waitlistError && (
            <div className="text-sm text-red-600 text-center">
              {waitlistError}
            </div>
          )}

          {/* Registration button - only for open, non-full tournaments */}
          {canShowRegisterButton && (
            <Button
              onClick={handleRegisterClick}
              className="w-full mt-4"
            >
              Register Now
            </Button>
          )}

          {REGISTRATIONS_CLOSED && isRegistrationOpen && (
            <ClosedNotice className="mt-4 text-center text-turquoise-700" />
          )}

          {/* Waitlist buttons - for full tournaments */}
          {isFullyBooked && isRegistrationOpen && (
            <div className="mt-4 space-y-2">
              {isOnWaitlist ? (
                <>
                  <div className="flex items-center justify-center gap-2 text-sm text-turquoise-700 bg-turquoise-50 rounded-md py-2">
                    <ListOrdered className="h-4 w-4" />
                    <span>You're on the waitlist</span>
                  </div>
                  <Button
                    onClick={handleLeaveWaitlist}
                    variant="outline"
                    className="w-full border-red-300 text-red-600 hover:bg-red-50"
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <X className="h-4 w-4 mr-2" />
                    )}
                    Leave Waitlist
                  </Button>
                </>
              ) : (
                !REGISTRATIONS_CLOSED && (
                  <Button
                    onClick={handleJoinWaitlist}
                    className="w-full bg-orange-600 hover:bg-orange-700"
                    disabled={actionLoading}
                  >
                    {actionLoading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <ListOrdered className="h-4 w-4 mr-2" />
                    )}
                    Join Waitlist
                  </Button>
                )
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
